import { useState, useCallback } from 'react';
import api from 'api';
import { generateURL, copyToClipboard } from 'helpers';
import { useToast } from 'hooks';
import { useIntl } from 'react-intl';
import { API_USER_INVITE_LINKS } from 'constants/api';
import { errorTexts } from 'i18n';
import { AxiosError, AxiosResponse } from 'axios';

const getInviteLink = (
  userId: number,
): Promise<
  AxiosResponse<{ inviteLink: string; expiresAt: string; isNew: boolean }>
> => api.axios.get(generateURL(API_USER_INVITE_LINKS, { userId }));

const getErrorMessage = (error: AxiosError<{ description: string }>) => ({
  ...errorTexts.somethingWentWrongContactSupport,
  values: {
    code: error.message,
    description: error.response?.data.description,
  },
});

export default function useInviteLink(userId: number) {
  const [loading, setLoading] = useState(false);
  const showMessage = useToast();
  const { formatMessage } = useIntl();

  const requestInviteLink = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getInviteLink(userId);
      copyToClipboard(data.inviteLink);
      showMessage({
        severity: 'success',
        message: formatMessage({
          id: 'pages.scorecards.inviteLinkCopied',
          defaultMessage: 'Invite link copied to clipboard',
        }),
      });
    } catch (error) {
      showMessage({
        severity: 'error',
        message: getErrorMessage(error as AxiosError<{ description: string }>),
      });
    } finally {
      setLoading(false);
    }
  }, [userId, showMessage, formatMessage]);

  return { requestInviteLink, loading };
}
